import { fetchWithTimeout, responseError, readJsonResponse } from './apiRequest';
import { loadCheckoutReceipt } from './checkoutReceipt';
import { APP_BASENAME, navigateOutsideApp } from './appNavigation';
// webapp/src/Profile.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "./supabase";
import { useAuth } from "./AuthContext";
import Modal from "./Modal";
import {
  FiUser,
  FiMail,
  FiLock,
  FiLogOut,
  FiAlertCircle,
  FiRefreshCw,
  FiTrash2,
  FiCheckCircle,
  FiCreditCard,
  FiArrowLeft,
  FiChevronDown,
  FiShield,
  FiZap,
  FiInfo,
  FiKey,
  FiPlus,
  FiCopy,
  FiTrash,
  FiList,
  FiCpu,
} from "react-icons/fi";
import "./Profile.css";
import CONFIG from "./config";
import {
  classifyError, diagnosticErrorCode,
  resolveGoogleAnalyticsAttribution,
  getMetaBrowserAttribution,
  getRedditBrowserAttribution,
  trackEvent,
  trackDiagnostic,
  requestMeasurements,
  trackEventWithCta,
  trackMetaInitiateCheckout,
  trackPremiumActivated,
  trackProductIntentEvent,
  trackUpgradeClick,
} from "./analytics";
import {
  buildCheckoutAttribution,
  clearCheckoutAttribution,
  getSavedCheckoutAttribution,
  storeCheckoutAttribution,
} from "./authAttribution";
import { useI18n } from "./i18n";
import { loadSignupAttribution } from './signupAttribution';
import BillingIntervalSelector from "./BillingIntervalSelector";
import InvoiceHistory from "./InvoiceHistory";
import { BILLING_INTERVALS, getBillingPriceSummary, pricing } from "./pricing";

// Resolve and sanitize API base (strip trailing slash)
const rawBase =
  CONFIG.API_BASE ||
  CONFIG.API_URL ||
  process.env.REACT_APP_API_BASE ||
  process.env.VITE_API_BASE ||
  "";
const API_BASE = rawBase.replace(/\/+$/, "");

function Profile() {
  const { t, language } = useI18n();
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const [quotaError, setQuotaError] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingError, setBillingError] = useState(false);
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [portalPending, setPortalPending] = useState(false);
  const checkoutRequestRef = useRef(false);
  const portalRequestRef = useRef(false);
  const premiumOfferRef = useRef(null);
  const premiumOfferSeen = useRef(false);
  const [premiumSource, setPremiumSource] = useState(null);
  const [stripeSubscriptionStatus, setStripeSubscriptionStatus] = useState(null);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  const [cancellationScheduled, setCancellationScheduled] = useState(false);
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);

  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [billingInterval, setBillingInterval] = useState(BILLING_INTERVALS.MONTHLY);

  // API keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeysError, setApiKeysError] = useState(false);
  const [apiKeyCreating, setApiKeyCreating] = useState(false);
  const apiKeyRequestRef = useRef(false);
  const [apiToolsOpen, setApiToolsOpen] = useState(false);
  const apiToolsTouched = useRef(false);
  const [newKeyLabel, setNewKeyLabel] = useState("");
  const [justCreatedKey, setJustCreatedKey] = useState(null); // {api_key, key_id, label}
  const [apiQuota, setApiQuota] = useState(null);
  const [apiQuotaLoading, setApiQuotaLoading] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);
  const [apiLogsVisible, setApiLogsVisible] = useState(false);
  const [apiLogsLoading, setApiLogsLoading] = useState(false);
  const [apiPlanModalOpen, setApiPlanModalOpen] = useState(false);
  const [apiPlanUpgrading, setApiPlanUpgrading] = useState(false);
  const [apiPlanTrialGrant, setApiPlanTrialGrant] = useState(null);
  const [apiPlanTrialLoading, setApiPlanTrialLoading] = useState(false);
  const [overageCapInput, setOverageCapInput] = useState("");
  const [overageSaving, setOverageSaving] = useState(false);

  const navigate = useNavigate();
  const checkoutActivationTrackedRef = useRef(false);
  const profileViewTrackedRef = useRef(false);
  const {
    resetReauthenticationStatus,
    setNextPageAfterReauth,
    checkPremiumStatus,
    isPremium: contextIsPremium,
    accountReady,
  } = useAuth();
  // The backend quota also confirms paid access if the premium-status RPC is unavailable.
  const isPremium = contextIsPremium || quota?.limit === "unlimited" || ["premium", "api"].includes(apiQuota?.plan);

  // Authorized fetch to Flask backend
  const apiFetch = useCallback(async (path, options = {}) => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
      if (!token) throw new Error("No active session");

    const res = await fetchWithTimeout(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const body = await readJsonResponse(res, { allowEmpty: true });
    if (!res.ok) {
      const msg = body?.error || body?.message || res.statusText;
      throw responseError(res, body, msg);
    }
    return body;
  }, []);

  // Get user data
  useEffect(() => {
    async function fetchUserData() {
      try {
        const { data } = await supabase.auth.getUser();
        const user = data?.user;
        if (user) {
          setCurrentUser({
            id: user.id,
            email: user.email,
            emailVerified: Boolean(user.email_confirmed_at),
            isEmailSignIn: user.app_metadata?.provider === "email",
            provider: user.app_metadata?.provider || "email",
          });
        }
      } catch (error) {
        console.warn("Error fetching user:", error.message || error);
      } finally {
        setUserLoading(false);
      }
    }
    fetchUserData();
  }, []);

  // Load billing source (Apple vs Stripe) from Supabase
  const fetchBillingSource = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("premium_source, stripe_customer_id, stripe_subscription_status, stripe_cancel_at_period_end, stripe_current_period_end")
        .eq("id", currentUser.id)
        .maybeSingle();
      if (error || !data) throw error || new Error("Billing details unavailable");
      setPremiumSource(data.premium_source || null);
      setStripeSubscriptionStatus(data.stripe_subscription_status || null);
      setHasStripeCustomer(Boolean(data.stripe_customer_id));
      setCancellationScheduled(Boolean(data.stripe_cancel_at_period_end));
      setSubscriptionEndsAt(data.stripe_current_period_end || null);
      setBillingError(false);
    } catch (error) {
      console.warn("Error fetching billing source:", error.message || error);
      setBillingError(true);
      trackDiagnostic("profile_load_error", error, { operation: "billing_source" });
    } finally {
      setBillingLoaded(true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchBillingSource();
    }
  }, [currentUser, contextIsPremium, fetchBillingSource]);

  useEffect(() => {
    if (!currentUser || profileViewTrackedRef.current) return;
    profileViewTrackedRef.current = true;
    trackEvent("profile_settings_view", {
      source: "profile_route",
    });
  }, [currentUser]);

  // Stripe success polling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    if (sessionId) {
      const checkoutAttribution = getSavedCheckoutAttribution();
      trackEventWithCta("checkout_return", {
        ...checkoutAttribution,
        source: "stripe",
        billing_provider: "stripe",
        return_surface: "profile",
        has_session_id: true,
      });
      const delays = [1000, 3000, 5000, 8000];
      delays.forEach((d) => {
        setTimeout(async () => {
          try {
            const premiumActive = await checkPremiumStatus(true);
            if (!premiumActive || checkoutActivationTrackedRef.current) return;
            const receipt = await loadCheckoutReceipt(sessionId);
            if (!receipt || checkoutActivationTrackedRef.current) return;
            const didTrack = trackPremiumActivated({
              ...checkoutAttribution,
              ...receipt,
              source: "profile",
              billing_provider: "stripe",
            });
            checkoutActivationTrackedRef.current = true;
            if (didTrack) clearCheckoutAttribution();
          } catch (error) {
            trackDiagnostic('checkout_receipt_error', error, { operation: 'checkout_receipt' });
          }
        }, d);
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [checkPremiumStatus]);

  // --- QUOTA: call backend /quota
  const fetchQuota = useCallback(async () => {
    try {
      const data = await apiFetch("/quota", { method: "GET" });
      setQuota(data);
      setQuotaError(false);
    } catch (error) {
      console.error("Error fetching quota:", error);
      setQuotaError(true);
      trackDiagnostic("quota_load_error", error, { source: "profile", operation: "quota" });
    }
  }, [apiFetch]);

  useEffect(() => {
    if (currentUser) {
      fetchQuota();
    }
  }, [currentUser, fetchQuota]);

  // --- API KEYS
  const loadApiKeys = useCallback(async () => {
    if (!currentUser) return;
    setApiKeysLoading(true);
    try {
      const data = await apiFetch("/v1/api-keys", { method: "GET" });
      setApiKeys(Array.isArray(data?.api_keys) ? data.api_keys : []);
      setApiKeysError(false);
    } catch (err) {
      console.error("Error loading API keys:", err);
      trackDiagnostic('profile_load_error', err, { source: 'profile', operation: 'api_keys' });
      setApiKeysError(true);
    } finally {
      setApiKeysLoading(false);
    }
  }, [apiFetch, currentUser]);

  const handleCreateApiKey = async () => {
    if (apiKeyRequestRef.current) return;
    apiKeyRequestRef.current = true;
    setApiKeyCreating(true);
    try {
      trackEvent("api_key_create_attempt");
      const body = await apiFetch("/v1/api-keys", {
        method: "POST",
        body: JSON.stringify({ label: newKeyLabel || "API Key" }),
      });
      // Show the new token ONCE
      setJustCreatedKey({
        api_key: body.api_key,
        key_id: body.key_id,
        label: body.label,
      });
      setNewKeyLabel("");
      await loadApiKeys();
      trackEvent("api_key_created");
    } catch (err) {
      trackEvent("api_key_create_error", { error_type: classifyError(err) });
      setModalTitle(t("profile.apiKeyError"));
      setModalContent(err.message || t("profile.apiCreateFailed"));
      setShowModal(true);
    } finally {
      apiKeyRequestRef.current = false;
      setApiKeyCreating(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm(t("profile.revokeConfirm"))) return;
    try {
      trackEvent("api_key_revoke_attempt");
      await apiFetch(`/v1/api-keys/${encodeURIComponent(keyId)}`, {
        method: "DELETE",
      });
      await loadApiKeys();
      trackEvent("api_key_revoked");
    } catch (err) {
      trackEvent("api_key_revoke_error", { error_type: classifyError(err) });
      setModalTitle(t("profile.apiKeyError"));
      setModalContent(err.message || t("profile.apiRevokeFailed"));
      setShowModal(true);
    }
  };

  useEffect(() => {
    if (currentUser) loadApiKeys();
  }, [currentUser, loadApiKeys]);

  const loadApiQuota = useCallback(async () => {
    if (!currentUser) return;
    setApiQuotaLoading(true);
    try {
      const data = await apiFetch("/v1/quota", { method: "GET" });
      setApiQuota(data);
      if (data?.overage) {
        setOverageCapInput(data.overage.cap_usd == null ? "" : String(data.overage.cap_usd));
      }
    } catch (err) {
      console.error("Error loading API quota:", err);
      trackDiagnostic('quota_load_error', err, { source: 'profile', operation: 'api_quota' });
      setApiQuota(null);
    } finally {
      setApiQuotaLoading(false);
    }
  }, [apiFetch, currentUser]);

  useEffect(() => {
    if (currentUser) loadApiQuota();
  }, [currentUser, loadApiQuota]);

  const loadApiPlanTrialGrant = useCallback(async () => {
    if (!currentUser) return null;
    setApiPlanTrialLoading(true);
    try {
      const data = await apiFetch("/billing/api-plan/trial-grant", { method: "GET" });
      setApiPlanTrialGrant(data || null);
      return data || null;
    } catch (err) {
      console.warn("Error loading API Plan trial grant:", err);
      setApiPlanTrialGrant(null);
      return null;
    } finally {
      setApiPlanTrialLoading(false);
    }
  }, [apiFetch, currentUser]);

  useEffect(() => {
    if (!apiToolsTouched.current && (apiKeys.length > 0 || apiQuota?.plan === "api" || Number(apiQuota?.used_seconds) > 0)) {
      setApiToolsOpen(true);
    }
  }, [apiKeys, apiQuota]);

  const loadApiLogs = async () => {
    if (!currentUser) return;
    setApiLogsLoading(true);
    try {
      const data = await apiFetch("/v1/transcriptions?limit=25", { method: "GET" });
      setApiLogs(Array.isArray(data?.items) ? data.items : []);
      setApiLogsVisible(true);
      trackEvent("api_logs_view");
    } catch (err) {
      setModalTitle("API Logs Error");
      setModalContent(err.message || "Failed to load API logs.");
      setShowModal(true);
    } finally {
      setApiLogsLoading(false);
    }
  };

  const handleSaveOverageCap = async () => {
    const capInput = overageCapInput.trim();
    const cap = capInput === "" ? null : Number(capInput);
    if (cap !== null && (!Number.isFinite(cap) || cap < 0)) {
      setModalTitle("Invalid Overage Cap");
      setModalContent("Enter a non-negative USD amount.");
      setShowModal(true);
      return;
    }
    const capUsd = capInput === "" ? null : cap;
    setOverageSaving(true);
    try {
      const data = await apiFetch("/v1/quota/overage-cap", {
        method: "PATCH",
        body: JSON.stringify({ enabled: true, cap_usd: capUsd }),
      });
      setApiQuota(data);
      trackEvent("api_overage_cap_updated", { cap_usd: capUsd });
    } catch (err) {
      setModalTitle("Overage Cap Error");
      setModalContent(err.message || "Failed to update overage cap.");
      setShowModal(true);
    } finally {
      setOverageSaving(false);
    }
  };

  // Sign out
  const handleSignOut = async () => {
    trackEvent("logout_attempt", { source: "profile" });
    const { error } = await supabase.auth.signOut();
    if (error) {
      trackEvent("logout_error", { source: "profile", error_type: classifyError(error) });
      console.error("Sign out error:", error);
    }
    else {
      resetReauthenticationStatus();
      navigate("/login");
    }
  };

  // Email verification
  const requestEmailVerification = async () => {
    if (currentUser && !currentUser.emailVerified) {
      try {
        trackEvent("email_verification_request");
        const { error } = await supabase.auth.resend({
          type: "signup",
          email: currentUser.email,
        });
        if (error) throw error;
        trackEvent("email_verification_sent");
        alert(t("profile.verificationSent"));
      } catch (error) {
        trackEvent("email_verification_error", { error_type: classifyError(error) });
        console.error("Error sending verification email:", error);
      }
    }
  };

  // Change email/password
  const handleChangePasswordClick = () => {
    if (currentUser?.isEmailSignIn) {
      trackEvent("security_change_start", { change_type: "password" });
      setNextPageAfterReauth("/dashboard/new-password");
      navigate("/dashboard/reauthenticate");
    }
  };

  const handleChangeEmailClick = () => {
    if (currentUser?.isEmailSignIn) {
      trackEvent("security_change_start", { change_type: "email" });
      setNextPageAfterReauth("/dashboard/change-email");
      navigate("/dashboard/reauthenticate");
    }
  };

  // Reset account (calls backend /reset_account)
  const handleResetAccountData = () => {
    trackEvent("account_reset_open");
    setIsModalOpen(true);
    setResponseMessage(null);
  };

  const handleResetConfirm = async () => {
    setIsProcessing(true);
    try {
      trackEvent("account_reset_attempt");
      const data = await apiFetch("/reset_account", { method: "POST" });
      setResponseMessage({
        type: "success",
        text: data.message || t("profile.resetSuccess"),
      });
      trackEvent("account_reset_success");
    } catch (error) {
      console.error("Error resetting account data:", error);
      trackEvent("account_reset_error", { error_type: classifyError(error) });
      setResponseMessage({
        type: "error",
        text:
          error.message || t("profile.resetFailed"),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setResponseMessage(null);
  };

  const handleDeleteAccount = () => {
    trackEvent("account_delete_start");
    setNextPageAfterReauth("/dashboard/delete-account-confirm");
    navigate("/dashboard/reauthenticate");
  };

  // Premium + billing portals
  const handlePremiumSubscription = async (
    ctaLocation = "profile_plan_card",
    selectedBillingInterval = billingInterval
  ) => {
    if (!showPremiumOffer || checkoutRequestRef.current) return;
    checkoutRequestRef.current = true;
    setCheckoutPending(true);
    const priceSummary = getBillingPriceSummary(selectedBillingInterval);
    let checkoutParams = {};
    try {
      await loadSignupAttribution(currentUser).catch(error => console.warn('Checkout attribution warning:', error.message));
      checkoutParams = buildCheckoutAttribution({
        checkout_source: "profile",
        source: "profile",
        cta_location: ctaLocation,
        checkout_cta_location: ctaLocation,
        plan: "free",
        billing_interval: priceSummary.billingInterval,
        plan_intent: `premium_${priceSummary.billingInterval}`,
        offer_variant: "profile_v1",
      }, currentUser);
      trackUpgradeClick(checkoutParams);
      trackEventWithCta("begin_checkout", checkoutParams);
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        const { data: refreshed } = await supabase.auth.refreshSession();
        if (!refreshed.session) throw new Error("No active session");
      }
      const token = (await supabase.auth.getSession()).data.session.access_token;

      const response = await fetchWithTimeout(CONFIG.CREATE_CHECKOUT_SESSION, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          app_base_path: APP_BASENAME,
          billing_interval: priceSummary.billingInterval,
          attribution: checkoutParams,
          ga: await resolveGoogleAnalyticsAttribution(),
          meta: getMetaBrowserAttribution(),
          reddit: getRedditBrowserAttribution(),
        }),
      }, 30000);
      if (!response.ok) {
        const e = await readJsonResponse(response);
        throw responseError(response, e, "Subscription error");
      }
      const checkoutData = await response.json();
      trackMetaInitiateCheckout({
        content_category: "subscription",
        source: "profile",
        billing_interval: priceSummary.billingInterval,
      });
      if (!isPremium && quota && typeof quota.limit === "number") {
        trackProductIntentEvent("checkout_started", {
          ...checkoutParams,
          plan: "free",
          content_category: "subscription",
          billing_provider: "stripe",
        }, {
          userId: currentUser?.id,
        });
      }
      storeCheckoutAttribution(checkoutParams, isPremium ? null : currentUser);
      trackEventWithCta("checkout_redirect", checkoutParams);
      navigateOutsideApp(checkoutData.url);
    } catch (error) {
      if (error?.code === 'request_cancelled') {
        trackDiagnostic('checkout_error', error, { operation: 'checkout' });
        return;
      }
      console.error("Premium subscription error:", error);
      trackEventWithCta("checkout_error", {
        ...checkoutParams,
        error_type: classifyError(error),
        error_code: diagnosticErrorCode(error),
        operation: 'checkout', ...requestMeasurements(error),
        ...(error.status ? { status_code: error.status } : {}),
      });
      setModalTitle(t("profile.subscriptionError"));
      setModalContent(t("profile.subscriptionFailed", { message: error.message }));
      setShowModal(true);
    } finally {
      checkoutRequestRef.current = false;
      setCheckoutPending(false);
    }
  };

  const handleManageSubscription = async () => {
    if (portalRequestRef.current) return;
    portalRequestRef.current = true;
    setPortalPending(true);
    try {
      trackEvent("billing_portal_open_attempt");
      const canUseStripePortal =
        hasStripeCustomer ||
        premiumSource === "stripe" ||
        premiumSource === "both" ||
        Boolean(stripeSubscriptionStatus);
      if (isPremium && !canUseStripePortal) {
        trackEvent("billing_portal_unavailable", { premium_source: premiumSource || "unknown" });
        setModalTitle(t("profile.manageTitle"));
        setModalContent(
          t("profile.appleManaged")
        );
        setShowModal(true);
        return;
      }
      const { data } = await supabase.auth.getSession();
      if (!data.session) throw new Error("No active session");
      const token = data.session.access_token;

      const response = await fetch(CONFIG.CREATE_BILLINGPORTAL_SESSION, {
        method: "POST",
        body: JSON.stringify({ app_base_path: APP_BASENAME }),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const e = await response.json();
        throw new Error(e.error || "Billing portal error");
      }
      const billingData = await response.json();
      trackEvent("billing_portal_redirect");
      navigateOutsideApp(billingData.url);
    } catch (error) {
      console.error("Error managing subscription:", error);
      trackEvent("billing_portal_error", { error_type: classifyError(error) });
      setModalTitle(t("profile.billingError"));
      setModalContent(t("profile.billingFailed", { message: error.message }));
      setShowModal(true);
    } finally {
      portalRequestRef.current = false;
      setPortalPending(false);
    }
  };

  const handleOpenApiPlanModal = async () => {
    await loadApiPlanTrialGrant();
    setApiPlanModalOpen(true);
  };

  const handleApiPlanUpgrade = async () => {
    setApiPlanUpgrading(true);
    try {
      trackEvent("api_plan_upgrade_attempt");
      const data = await apiFetch("/billing/api-plan/upgrade", {
        method: "POST",
        body: JSON.stringify({}),
      });
      trackEvent("api_plan_upgrade_success");
      setApiPlanModalOpen(false);
      setApiPlanTrialGrant(null);
      await fetchBillingSource();
      await loadApiQuota();
      if (data?.trial_applied) {
        setModalTitle("API Plan Trial Started");
        setModalContent(
          `Your 60-day API Plan trial is active. You will not be charged the API Plan subscription fee until ${formatReset(data.trial_ends_at)}. Your 100 included API audio hours reset monthly during the trial. Overage can still be charged if you use more than the included monthly hours.`
        );
      } else {
        setModalTitle("API Plan Upgrade Started");
        setModalContent("Your subscription is being updated to the API Plan. You can manage it through the subscription portal.");
      }
      setShowModal(true);
    } catch (error) {
      trackEvent("api_plan_upgrade_error", { error_type: classifyError(error) });
      setModalTitle("API Plan Upgrade Error");
      setModalContent(error.message || "Failed to upgrade to the API Plan.");
      setShowModal(true);
    } finally {
      setApiPlanUpgrading(false);
    }
  };

  // Helpers
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      trackEvent("api_key_copy");
      setModalTitle(t("profile.copiedTitle"));
      setModalContent(t("profile.copiedBody"));
      setShowModal(true);
    } catch {
      trackEvent("api_key_copy_error");
      setModalTitle(t("profile.copyFailedTitle"));
      setModalContent(t("profile.copyFailedBody"));
      setShowModal(true);
    }
  };

  const fmt = (v) =>
    v ? new Date(v.replace(" ", "T")).toLocaleString(language) : "-";
  const formatHours = (seconds) => {
    const value = Number(seconds || 0) / 3600;
    if (!Number.isFinite(value)) return "-";
    return `${value.toFixed(value >= 10 ? 1 : 2)}h`;
  };
  const formatReset = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString(language);
  };

  // Progress bar for free plan
  let progressPercent = 0;
  if (quota && quota.limit !== "unlimited") {
    progressPercent = Math.max(0, Math.min(
      100,
      ((quota.used || 0) / (quota.limit || 1)) * 100
    ));
  }
  const billingPriceSummary = getBillingPriceSummary(billingInterval);
  const billingLabels = {
    ariaLabel: t("billing.intervalLabel"),
    monthly: t("billing.monthly"),
    annual: t("billing.annual"),
  };
  const billingDetail = billingInterval === BILLING_INTERVALS.ANNUAL
    ? t("billing.billedAnnual", { annualTotal: billingPriceSummary.annualTotalPrice })
    : t("billing.billedMonthly");
  const isApiPlan = apiQuota?.plan === "api";
  const isConfirmedFreePlan = accountReady && billingLoaded && !billingError && !quotaError && typeof quota?.limit === "number";
  const showPremiumOffer = !isPremium && isConfirmedFreePlan && !["past_due", "unpaid", "incomplete"].includes(stripeSubscriptionStatus);
  const isAppleOnly = premiumSource === "apple" && !hasStripeCustomer && !stripeSubscriptionStatus;
  const needsBillingAttention = ["past_due", "unpaid", "incomplete"].includes(stripeSubscriptionStatus);
  const planLabel = isPremium ? (isApiPlan ? t("profile.apiPlan") : t("overview.premium"))
    : isConfirmedFreePlan ? t("profile.freePlan") : t("workspace.yourPlan");

  useEffect(() => {
    if (!showPremiumOffer || premiumOfferSeen.current) return;
    const recordView = () => {
      if (premiumOfferSeen.current) return;
      premiumOfferSeen.current = true;
      trackEvent("premium_offer_view", {
        cta_location: "profile_plan_card", offer_variant: "profile_v1",
        billing_interval: billingInterval, account_type: "registered",
      });
    };
    if (!window.IntersectionObserver) { recordView(); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { recordView(); observer.disconnect(); }
    }, { threshold: 0.4 });
    if (premiumOfferRef.current) observer.observe(premiumOfferRef.current);
    return () => observer.disconnect();
  }, [showPremiumOffer, billingInterval]);

  const handleBillingIntervalChange = interval => {
    setBillingInterval(interval);
    trackEvent("premium_plan_compare", { cta_location: "profile_plan_card", offer_variant: "profile_v1", billing_interval: interval });
  };
  const retryPlanDetails = () => Promise.all([fetchBillingSource(), fetchQuota(), loadApiQuota(), checkPremiumStatus(true)]);
  const apiPlanTrialEligible = Boolean(apiPlanTrialGrant?.eligible);
  const apiPlanTrialEndsAt = apiPlanTrialGrant?.trial_ends_at;
  const formattedApiPlanTrialEnd = formatReset(apiPlanTrialEndsAt);
  const apiPlanUpgradeContent = (
    <div className="api-plan-modal-copy">
      <p className="api-plan-modal-copy__lead">
        Add production-ready API capacity to your Premium account without losing unlimited web transcriptions.
      </p>

      <div className="api-plan-modal-copy__hero">
        <span>{apiPlanTrialEligible ? "Trial available" : "API Plan"}</span>
        <strong>
          {apiPlanTrialEligible ? "Try the API Plan for 60 days" : "100 API audio hours included"}
        </strong>
        <p>
          {apiPlanTrialEligible
            ? `No API Plan subscription fee until ${formattedApiPlanTrialEnd}. Afterwards, 49 USD/month.`
            : "$49/month with predictable included API usage."}
        </p>
      </div>

      <div className="api-plan-modal-copy__grid">
        <div>
          <span>Included every month</span>
          <strong>100 API audio hours</strong>
        </div>
        <div>
          <span>After included hours</span>
          <strong>$0.49/audio hour</strong>
        </div>
      </div>

      <ul className="api-plan-modal-copy__benefits">
        <li>
          <FiCheckCircle />
          <span>Keep unlimited Premium transcriptions in the web app.</span>
        </li>
        <li>
          <FiCheckCircle />
          <span>Run automations, batch jobs, and product workflows from the API.</span>
        </li>
        <li>
          <FiCheckCircle />
          <span>Overage keeps API jobs running and you can set a monthly cap after upgrading.</span>
        </li>
      </ul>

      <p className="api-plan-modal-copy__note">
        {apiPlanTrialEligible
          ? "This replaces your current Premium subscription. API overage can still be charged if your API usage goes above the cap you set."
          : "This updates your current Premium subscription with prorated billing. Web app transcriptions do not count against API quota."}
      </p>
    </div>
  );

  return (
    <div className="profile-container">
      <header className="profile-header">
        <div>
          <Link to="/dashboard" className="profile-back-link" onClick={() => {
            if (isPremium) trackEvent("premium_workflow_return", { source: "profile" });
          }}><FiArrowLeft aria-hidden="true" />{t("profile.backToWorkspace")}</Link>
          <h1>{t("profile.title")}</h1>
          <p>{t("profile.subtitle")}</p>
        </div>
        {currentUser && <span className={`profile-plan-badge ${isPremium ? "is-premium" : ""}`}>
          {isPremium && <FiCheckCircle aria-hidden="true" />}{planLabel}
        </span>}
      </header>

      {currentUser ? (
        <div className="profile-cards-wrapper">
          <section className="profile-card profile-card--identity" aria-labelledby="profile-account-title">
            <h2 className="section-title" id="profile-account-title"><FiUser aria-hidden="true" />{t("profile.accountSecurity")}</h2>
            <div className="profile-identity">
              <span className="profile-avatar" aria-hidden="true"><FiUser /></span>
              <div>
                <span className="profile-field-label">{t("common.email")}</span>
                <span className="user-info-value">{currentUser.email}</span>
                <span className={`profile-verification ${currentUser.emailVerified ? "is-verified" : ""}`}>
                  {currentUser.emailVerified && <FiCheckCircle aria-hidden="true" />}
                  {t(currentUser.emailVerified ? "profile.emailVerified" : "profile.emailPending")}
                </span>
              </div>
            </div>

            {currentUser.isEmailSignIn ? (
              <div className="profile-security">
                {!currentUser.emailVerified && <div className="profile-verification-notice">
                  <p>{t("profile.emailUnverified")}</p>
                  <button type="button" className="btn secondary-btn" onClick={requestEmailVerification}>
                    <FiMail aria-hidden="true" />{t("profile.resendVerification")}
                  </button>
                </div>}
                <div className="profile-security-actions">
                  <button type="button" className="btn secondary-btn" onClick={handleChangeEmailClick}>
                    <FiMail aria-hidden="true" />{t("profile.changeEmail")}
                  </button>
                  <button type="button" className="btn secondary-btn" onClick={handleChangePasswordClick}>
                    <FiLock aria-hidden="true" />{t("profile.changePassword")}
                  </button>
                </div>
                <p className="profile-muted profile-security-note"><FiShield aria-hidden="true" />{t("profile.securityHint")}</p>
              </div>
            ) : <p className="profile-muted profile-provider-note">
              {t("profile.providerManaged", { provider: currentUser.provider === "google" ? "Google" : currentUser.provider === "apple" ? "Apple" : currentUser.provider })}
            </p>}
            <div className="profile-signout">
              <button type="button" className="btn secondary-btn" onClick={handleSignOut}><FiLogOut aria-hidden="true" />{t("profile.signOut")}</button>
            </div>
          </section>

          <section className={`profile-card profile-card--user ${isPremium ? "is-premium" : ""}`} aria-labelledby="profile-plan-title">
            <h2 className="section-title" id="profile-plan-title"><FiCreditCard aria-hidden="true" />{t("profile.planBilling")}</h2>
            <div className="profile-plan-heading">
              <h3>{planLabel}</h3>
              {isPremium && <span className="profile-plan-status"><FiCheckCircle aria-hidden="true" />{t("profile.active")}</span>}
            </div>

            {isPremium ? <>
              <p className="profile-muted">{t("profile.premiumWebAccess")}</p>
              <ul className="profile-plan-benefits">
                {[t("overview.proFeature1"), t("workspace.premiumLimits"), t("overview.proFeature4")].map(benefit =>
                  <li key={benefit}><FiCheckCircle aria-hidden="true" />{benefit}</li>)}
              </ul>
              {isApiPlan && <p className="profile-muted">{t("profile.apiPlanAccess")}</p>}
              {isAppleOnly && <div className="info-text"><FiInfo aria-hidden="true" /><span>{t("profile.appleManagedShort")}</span></div>}
              {cancellationScheduled && subscriptionEndsAt && Number.isFinite(Date.parse(subscriptionEndsAt)) &&
                <div className="info-text" role="status"><FiInfo aria-hidden="true" /><span>{t("profile.cancellationScheduled", {
                  date: new Date(subscriptionEndsAt).toLocaleDateString(language, { year: "numeric", month: "long", day: "numeric" }),
                })}</span></div>}
            </> : isConfirmedFreePlan ? <div className="profile-free-usage">
              <p>{t("workspace.freeRemaining", { remaining: quota.remaining, limit: quota.limit })}</p>
              <div className="quota-bar-container" role="progressbar" aria-label={t("workspace.dailyAllowance")}
                aria-valuemin={0} aria-valuemax={quota.limit} aria-valuenow={Math.max(0, Math.min(quota.used || 0, quota.limit))}>
                <div className={`quota-bar-fill ${progressPercent > 80 ? "quota-high" : progressPercent > 50 ? "quota-medium" : "quota-low"}`}
                  style={{ width: `${progressPercent}%` }} />
              </div>
              <p className="profile-muted">{t("workspace.freeLimits")}</p>
            </div> : !billingError && !quotaError && <p className="profile-muted" role="status">{t("common.loading")}</p>}

            {(billingError || (quotaError && !isPremium)) && <div className="profile-load-error">
              <p role="alert">{t("profile.planLoadFailed")}</p>
              <button type="button" className="btn secondary-btn" onClick={retryPlanDetails}><FiRefreshCw aria-hidden="true" />{t("profile.retryInvoices")}</button>
            </div>}
            {needsBillingAttention && <div className="profile-billing-alert" role="status"><FiAlertCircle aria-hidden="true" />{t("profile.billingNeedsAttention")}</div>}

            {hasStripeCustomer && <div className="profile-billing-actions">
              <InvoiceHistory key={currentUser.id} apiFetch={apiFetch} />
              <button type="button" className="btn primary-btn" onClick={handleManageSubscription} disabled={portalPending}>
                <FiCreditCard aria-hidden="true" />{t(portalPending ? "profile.openingBilling" : "profile.manageSubscription")}
              </button>
              <p className="profile-muted">{t("profile.billingHint")}</p>
            </div>}

            {showPremiumOffer && <div className="profile-upgrade-offer" ref={premiumOfferRef}>
              <h3><FiZap aria-hidden="true" />{t("profile.upgradeTitle")}</h3>
              <p className="profile-muted">{t("workspace.premiumIntro")}</p>
              <ul className="profile-plan-benefits">
                {[t("overview.proFeature1"), t("workspace.premiumLimits"), t("overview.proFeature4")].map(benefit =>
                  <li key={benefit}><FiCheckCircle aria-hidden="true" />{benefit}</li>)}
              </ul>
              <BillingIntervalSelector value={billingInterval} onChange={handleBillingIntervalChange} labels={billingLabels}
                saveLabel={t("billing.savePercent", { percent: pricing.annualDiscountPercent })}
                className="billing-interval-toggle--profile" disabled={checkoutPending} />
              <div className="profile-upgrade-price">
                <span className="profile-upgrade-price__main"><strong>{billingPriceSummary.monthlyDisplayPrice}</strong><span>{t("billing.perMonth")}</span></span>
                <span className="profile-upgrade-price__detail">{billingDetail}</span>
              </div>
              <button type="button" className="btn upgrade-btn" onClick={() => handlePremiumSubscription("profile_plan_card", billingInterval)} disabled={checkoutPending}>
                <FiZap aria-hidden="true" />{t(checkoutPending ? "profile.openingCheckout" : "profile.startPremium")}
              </button>
            </div>}

          </section>

          <section className="profile-card">
            <h2>MCP</h2>
            <p>{t('profile.mcpDescription')}</p>
            <p><Link to="/mcp/connections">{t('profile.mcpConnections')}</Link>{' · '}<a href="https://instanttranscriber.com/mcp">{t('profile.mcpSetup')}</a></p>
          </section>

          <details className="profile-card profile-card--api" open={apiToolsOpen} onToggle={event => {
            const open = event.currentTarget.open;
            if (open !== apiToolsOpen) {
              apiToolsTouched.current = true;
              setApiToolsOpen(open);
            }
          }}>
            <summary className="profile-disclosure">
              <FiKey className="profile-disclosure-icon" aria-hidden="true" />
              <span><span className="profile-disclosure-title">{t("profile.developerTools")}</span>
                <span className="profile-muted">{t("profile.developerHint")}</span></span>
              <FiChevronDown className="profile-disclosure-chevron" aria-hidden="true" />
            </summary>
            <div className="profile-disclosure-content">

            <div className="info-text">
              <FiInfo />
              <span>
                {t("profile.apiDescription")}
              </span>
            </div>

            <div className="api-quota-panel">
              <div className="api-quota-header">
                <strong>{t("profile.apiMonthlyQuota")}</strong>
                <button
                  className="btn secondary-btn compact-btn"
                  onClick={loadApiQuota}
                  disabled={apiQuotaLoading}
                >
                  <FiRefreshCw /> {t(apiQuotaLoading ? "profile.refreshing" : "profile.refresh")}
                </button>
              </div>
              {apiQuota ? (
                <>
                  <div className="api-quota-grid">
                    <div>
                      <span>{t("profile.plan")}</span>
                      <strong>{apiQuota.plan === "api" ? t("profile.apiPlan") : apiQuota.plan === "premium" ? t("overview.premium") : t("profile.freePlan")}</strong>
                    </div>
                    <div>
                      <span>{t("profile.included")}</span>
                      <strong>{formatHours(apiQuota.total_included_seconds)}</strong>
                    </div>
                    <div>
                      <span>{t("profile.used")}</span>
                      <strong>{formatHours(apiQuota.used_seconds)}</strong>
                    </div>
                    <div>
                      <span>{t("profile.remaining")}</span>
                      <strong>{formatHours(apiQuota.remaining_included_seconds)}</strong>
                    </div>
                  </div>
                  <div className="api-quota-reset">
                    {t("profile.resets")}: {formatReset(apiQuota.reset_at)} ({apiQuota.timezone || "UTC"})
                    {Number(apiQuota.granted_seconds || 0) > 0 && (
                      <span> · Includes {formatHours(apiQuota.granted_seconds)} manual grant</span>
                    )}
                  </div>
                  {apiQuota.overage?.available && (
                    <div className="api-overage-box">
                      <div>
                        <strong>API overage</strong>
                        <span>
                          ${Number(apiQuota.overage.used_usd || 0).toFixed(2)} used at ${apiQuota.overage.rate_usd_per_hour}/audio hour.
                          {" "}
                          {apiQuota.overage.enabled
                            ? apiQuota.overage.cap_usd == null
                              ? "No monthly cap set."
                              : `$${Number(apiQuota.overage.cap_usd).toFixed(2)} monthly cap.`
                            : "Overage disabled."}
                        </span>
                      </div>
                      <div className="api-overage-controls">
                        <button
                          type="button"
                          className={`quota-chip ${overageCapInput.trim() === "" ? "selected" : ""}`}
                          onClick={() => setOverageCapInput("")}
                        >
                          No cap
                        </button>
                        {[0, 50, 100, 200].map((value) => (
                          <button
                            key={value}
                            type="button"
                            className={`quota-chip ${overageCapInput.trim() !== "" && Number(overageCapInput) === value ? "selected" : ""}`}
                            onClick={() => setOverageCapInput(String(value))}
                          >
                            ${value}
                          </button>
                        ))}
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={overageCapInput}
                          onChange={(e) => setOverageCapInput(e.target.value)}
                          placeholder="Custom cap"
                          aria-label={t("profile.monthlyCap")}
                          className="input overage-input"
                        />
                        <button
                          className="btn primary-btn compact-btn"
                          onClick={handleSaveOverageCap}
                          disabled={overageSaving}
                        >
                          {overageSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="api-quota-reset" role="status">{t(apiQuotaLoading ? "common.loading" : "profile.apiQuotaUnavailable")}</div>
              )}
            </div>

            {isPremium && !isApiPlan && <div className="profile-api-plan-link">
              <p className="profile-muted">{t("profile.apiPlanHint")}</p>
              <button type="button" className="btn secondary-btn" onClick={handleOpenApiPlanModal} disabled={apiPlanTrialLoading}>
                <FiCpu aria-hidden="true" />{t(apiPlanTrialLoading ? "profile.checkingApiPlan" : "profile.exploreApiPlan")}
              </button>
            </div>}

            <form className="form-inline" onSubmit={event => { event.preventDefault(); handleCreateApiKey(); }}>
              <label className="profile-field-label" htmlFor="profile-api-key-label">{t("profile.apiKeyLabel")}</label>
              <input
                id="profile-api-key-label"
                type="text"
                placeholder={t("profile.apiPlaceholder")}
                value={newKeyLabel}
                onChange={(e) => setNewKeyLabel(e.target.value)}
                className="input"
              />
              <button type="submit" className="btn primary-btn" disabled={apiKeyCreating}>
                <FiPlus /> {t(apiKeyCreating ? "profile.creatingApiKey" : "profile.createApiKey")}
              </button>
            </form>

            <div className="form-group">
              <button
                className="btn secondary-btn"
                onClick={loadApiLogs}
                disabled={apiLogsLoading}
              >
                <FiList /> {t(apiLogsLoading ? "profile.loadingApiLogs" : "profile.viewApiLogs")}
              </button>
            </div>

            {apiLogsVisible && (
              <div className="api-logs-panel">
                <div className="api-quota-header">
                  <strong>{t("profile.recentApiJobs")}</strong>
                  <button className="btn secondary-btn compact-btn" onClick={() => setApiLogsVisible(false)}>
                    {t("profile.hide")}
                  </button>
                </div>
                {apiLogs.length === 0 ? (
                  <div className="api-quota-reset">{t("profile.noApiJobs")}</div>
                ) : (
                  <div className="api-logs-list">
                    {apiLogs.map((job) => (
                      <div className="api-log-row" key={job.id}>
                        <div>
                          <strong>{job.original_filename || "API transcription"}</strong>
                          <div className="api-quota-reset">
                            {job.status} · {formatHours(job.quota?.billed_seconds || 0)} billed · {fmt(job.created_at)}
                          </div>
                        </div>
                        <code>{job.id}</code>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {apiKeysError && <div className="profile-load-error">
              <p role="alert">{t("profile.apiKeysLoadFailed")}</p>
              <button type="button" className="btn secondary-btn" onClick={loadApiKeys} disabled={apiKeysLoading}>{t("profile.retryInvoices")}</button>
            </div>}
            {apiKeysLoading ? (
              <div style={{ marginTop: 12 }}>{t("profile.loadingKeys")}</div>
            ) : (
              <div className="api-keys-list">
                {apiKeys.length === 0 && !apiKeysError ? (
                  <div style={{ opacity: 0.8, marginTop: 8 }}>
                    {t("profile.noKeys")}
                  </div>
                ) : (
                  apiKeys.map((k) => (
                    <div className="api-key-row" key={k.key_id}>
                      <div className="api-key-meta">
                        <div>
                          <strong>{k.label}</strong> ({k.status})
                        </div>
                        <div>
                          Key ID: <code>{k.key_id}</code>
                        </div>
                        <div>{t("profile.created")} {fmt(k.created_at)}</div>
                        <div>{t("profile.lastUsed")} {fmt(k.last_used_at)}</div>
                      </div>
                      <div className="api-key-actions">
                        {!k.revoked_at && (
                          <button
                            className="btn danger-btn"
                            onClick={() => handleRevokeApiKey(k.key_id)}
                          >
                            <FiTrash /> {t("common.revoke")}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Show the token only once after creation */}
            {justCreatedKey && (
              <div className="new-key-banner">
                <div style={{ marginBottom: 6 }}>
                  <strong>{t("profile.newApiKey")}</strong>
                </div>
                <div className="new-key-token">
                  <code>{justCreatedKey.api_key}</code>
                </div>
                <div className="form-inline" style={{ marginTop: 8 }}>
                  <button
                    className="btn secondary-btn"
                    onClick={() => copy(justCreatedKey.api_key)}
                  >
                    <FiCopy /> {t("common.copy")}
                  </button>
                  <button
                    className="btn secondary-btn"
                    onClick={() => setJustCreatedKey(null)}
                  >
                    {t("common.done")}
                  </button>
                </div>
              </div>
            )}
            </div>
          </details>

          {/* -- Account Data Card (Reset/Delete) -- */}
          <details className="profile-card profile-card--account">
            <summary className="profile-disclosure">
              <FiShield className="profile-disclosure-icon" aria-hidden="true" />
              <span><span className="profile-disclosure-title">{t("profile.accountData")}</span>
                <span className="profile-muted">{t("profile.dataControlsHint")}</span></span>
              <FiChevronDown className="profile-disclosure-chevron" aria-hidden="true" />
            </summary>
            <div className="profile-disclosure-content">

            <div className="warning-text">
              <FiAlertCircle />
              <span>
                {t("profile.accountWarning")}
              </span>
            </div>

            <div className="form-group">
              <button className="btn danger-btn" onClick={handleResetAccountData}>
                <FiRefreshCw /> {t("profile.resetAccount")}
              </button>
            </div>
            <div className="form-group">
              <button className="btn danger-btn" onClick={handleDeleteAccount}>
                <FiTrash2 /> {t("profile.deleteAccount")}
              </button>
            </div>
            </div>
          </details>
        </div>
      ) : (
        <div className="profile-card">
          <div className={userLoading ? "profile-muted" : "warning-text"}>
            {!userLoading && <FiAlertCircle />}
            <span role="status">{t(userLoading ? "common.loading" : "profile.noUser")}</span>
          </div>
        </div>
      )}

      {/* Reset Account Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        title={t("profile.resetConfirmTitle")}
        message={t("profile.resetConfirmMessage")}
        onClose={handleCloseModal}
        onConfirm={handleResetConfirm}
        isProcessing={isProcessing}
        responseMessage={responseMessage}
        variant="confirm"
        confirmLabel={t("profile.resetConfirm")}
        cancelLabel={t("common.cancel")}
      />

      <Modal
        isOpen={apiPlanModalOpen}
        title="Upgrade to API Plan"
        message={apiPlanUpgradeContent}
        onClose={() => setApiPlanModalOpen(false)}
        onConfirm={handleApiPlanUpgrade}
        isProcessing={apiPlanUpgrading}
        variant="confirm"
        confirmLabel={apiPlanTrialEligible ? "Start 60-day API trial" : "Upgrade to API Plan"}
        confirmButtonClassName="upgrade-btn"
        modalClassName="api-plan-upgrade-modal"
        bodyClassName="api-plan-upgrade-modal__body"
        cancelLabel={t("common.cancel")}
      />

      {/* Generic info/error modal (copy success, API errors, etc.) */}
      {showModal && (
        <Modal
          isOpen={showModal}
          title={modalTitle}
          message={modalContent}
          onClose={() => setShowModal(false)}
          variant="info"
        />
      )}
    </div>
  );
}

export default Profile;
